<script setup lang="ts">
import Extension from "@/core/interfaces/extension/Extension";
import {useRoute, useRouter} from "vue-router";
import {useExtensionStore} from "@/stores/extension.store";
import {storeToRefs} from "pinia";
import {computed} from "vue";
import hospitalCityVideoThumbnail from "@/assets/images/hospital_city_video_thumbnail.png";
import ExtensionNavigationService from "@/extension/services/ExtensionNavigationService";
import {ExtensionGrid} from "@/extension/components/extension-grid";
import useSwr from "@/core/composables/swr/useSwr";
import {sharingApi} from "@/container";
import {Sharing} from "@/core/interfaces/Sharing";
import LightweightSharingItem from "@/hospital/components/sharing/LightweightSharingItem.vue";
import SharingService from "@/sharing/services/SharingService";
import HospitalReferralSection from "@/hospital/components/hospital-referral-section/HospitalReferralSection.vue";

const router = useRouter();
const route = useRoute();
const extensionStore = useExtensionStore();
const {extensions} = storeToRefs(extensionStore);
const {data, isLoading} = useSwr('sharings/ownerships', () => sharingApi().indexAsOwner());

/**
 * The hospital extensions.
 */
const hospitalExtensions = computed(() => {
    return extensions.value?.filter((extension) => {
        return extension.organization.entity_type === 'hospital';
    }) ?? [];
});


/**
 * Navigate to the extension details.
 */
const navigateToExtensionDetails = (extension: Extension) => {
    return ExtensionNavigationService.navigate(extension, router, route);
};

const show = () => {
    window.open('https://www.youtube.com/watch?v=23kNDrPCdaI', '_blank');
};

/**
 * Redirect to the sharable's hospital page.
 *
 * @param sharing
 */
const redirectToSharable = (sharing: Sharing) => {
    router.push({
        name: 'customer.hospitals',
        params: {
            customerId: sharing.sharable?.customer_id
        }
    });
};
</script>

<template>
  <div class="p-4 flex flex-col gap-4">
    <div class="flex flex-1 flex-row">
      <div class="flex flex-1 flex-col pr-5">
        <h1>Suivez tous vos patients en traitement à domicile</h1>
        <p>Retrouvez les patients faisant partie d'un programme d'accompagnement renforcé ville-hopital.</p>
      </div>
      <img
        :src="hospitalCityVideoThumbnail"
        class="cursor-pointer shadow-xl hover:opacity-75"
        @click="show"
      >
    </div>
    <ExtensionGrid
      title="Hôpitaux"
      :extensions="hospitalExtensions"
      @extension-click="(extension) => navigateToExtensionDetails(extension)"
    />
    <HospitalReferralSection />
    <div>
      <div class="mb-4">
        <h2>
          Patients suivis à l’Hôpital
        </h2>
      </div>
      <div v-if="isLoading" class="text-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
        <p class="mt-2 text-gray-600">
          Chargement des partages...
        </p>
      </div>
      <div v-else-if="data && data.length > 0" class="grid gap-4">
        <div v-for="sharing in data.sort(SharingService.sortSharings)" :key="sharing.id" class="cursor-pointer" @click="redirectToSharable(sharing)">
          <LightweightSharingItem :sharing="sharing" />
        </div>
      </div>
      <div v-else class="text-center py-8 text-gray-500">
        <p>Aucun partage de données trouvé.</p>
      </div>
    </div>
  </div>
</template>
